import React, { useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { ChevronLeft, ChevronRight, Play, Pause, ArrowRight } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const mediaUrl = (u) => (u ? (u.startsWith("http") ? u : `${BACKEND}${u}`) : "");

// Fixed 16:10 box; identical outer styling to the original hero image block.
const BOX = "relative h-64 w-full overflow-hidden rounded-2xl border border-gmoaa-border-subtle shadow-lg sm:h-80";

const prefersReducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export const HeroSlideshow = () => {
  const { lang } = useLang();
  const [images, setImages] = useState([]);
  const [seconds, setSeconds] = useState(3);
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const reduced = prefersReducedMotion();
  const touchX = useRef(null);

  useEffect(() => {
    api.slideshow()
      .then((d) => { setImages(d.images || []); setSeconds(d.seconds || 3); setLoaded(true); })
      .catch(() => setLoaded(true));
  }, []);

  const count = images.length;
  const go = useCallback((n) => setIdx((i) => (count ? (n + count) % count : 0)), [count]);
  const next = useCallback(() => go(idx + 1), [go, idx]);
  const prev = useCallback(() => go(idx - 1), [go, idx]);

  // Autoplay (never autoplay when reduced motion is preferred)
  useEffect(() => {
    if (reduced || !playing || count <= 1) return undefined;
    const ms = Math.min(Math.max(seconds, 2), 10) * 1000;
    const t = setTimeout(() => go(idx + 1), ms);
    return () => clearTimeout(t);
  }, [reduced, playing, count, seconds, idx, go]);

  // Preload the next image while the current one shows
  useEffect(() => {
    if (count <= 1) return;
    const nxt = images[(idx + 1) % count];
    const u = mediaUrl(nxt?.src?.w1000 || nxt?.src?.w1600);
    if (u) { const im = new Image(); im.src = u; }
  }, [idx, images, count]);

  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; setPlaying(false); };
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 40) (dx < 0 ? next() : prev());
    touchX.current = null;
  };

  // Empty / loading → logo on a plain cream panel
  if (!loaded || count === 0) {
    return (
      <div className={`${BOX} flex items-center justify-center bg-gmoaa-cream`} data-testid="hero-slideshow-empty">
        <img src="/gmoaa-logo.png" alt="GMOAA" className="h-24 w-24 object-contain opacity-90" />
        <noscript><img src="/gmoaa-logo.png" alt="GMOAA" style={{ height: 96 }} /></noscript>
      </div>
    );
  }

  return (
    <div
      className={BOX}
      data-testid="hero-slideshow"
      onMouseEnter={() => setPlaying(false)}
      onMouseLeave={() => setPlaying(true)}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      role="region"
      aria-roledescription="carousel"
      aria-label="Highlights"
    >
      {images.map((im, i) => {
        const srcSet = `${mediaUrl(im.src.w640)} 640w, ${mediaUrl(im.src.w1000)} 1000w, ${mediaUrl(im.src.w1600)} 1600w`;
        const caption = lang === "gu" ? im.caption_gu : im.caption_en;
        const cta = lang === "gu" ? (im.cta_label_gu || im.cta_label_en) : im.cta_label_en;
        const hasCta = !!(im.link && cta);
        const content = (
          <img
            src={mediaUrl(im.src.w1000)}
            srcSet={srcSet}
            sizes="(max-width: 1024px) 100vw, 50vw"
            alt={lang === "gu" ? (im.alt_gu || im.alt_en) : im.alt_en}
            loading={i === 0 ? "eager" : "lazy"}
            fetchPriority={i === 0 ? "high" : "auto"}
            decoding="async"
            className="h-full w-full object-cover"
            draggable={false}
          />
        );
        return (
          <div
            key={im.id}
            className="absolute inset-0"
            style={{ opacity: i === idx ? 1 : 0, transition: "opacity 600ms ease-in-out", pointerEvents: i === idx ? "auto" : "none" }}
            aria-hidden={i === idx ? "false" : "true"}
            data-testid={`hero-slide-${i}`}
          >
            {hasCta ? content : (im.link ? <a href={im.link} target="_blank" rel="noopener noreferrer">{content}</a> : content)}
            {(caption || hasCta) ? (
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-4 pb-8">
                {caption ? <p className="text-sm font-medium text-white">{caption}</p> : null}
                {hasCta ? (
                  <a href={im.link} target="_blank" rel="noopener noreferrer" data-testid={`slide-cta-${i}`}
                    className="mt-2 inline-flex items-center gap-1 rounded-full bg-gmoaa-gold px-3 py-1.5 text-xs font-semibold text-gmoaa-green transition-colors hover:bg-gmoaa-gold/90">
                    {cta} <ArrowRight size={13} />
                  </a>
                ) : null}
              </div>
            ) : null}
          </div>
        );
      })}

      {count > 1 && (
        <>
          <button data-testid="slide-prev" onClick={prev} aria-label="Previous slide"
            className="absolute left-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-white/80 text-gmoaa-green shadow hover:bg-white">
            <ChevronLeft size={18} />
          </button>
          <button data-testid="slide-next" onClick={next} aria-label="Next slide"
            className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-white/80 text-gmoaa-green shadow hover:bg-white">
            <ChevronRight size={18} />
          </button>
          {!reduced && (
            <button data-testid="slide-playpause" onClick={() => setPlaying((p) => !p)} aria-label={playing ? "Pause" : "Play"}
              className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full bg-white/80 text-gmoaa-green shadow hover:bg-white">
              {playing ? <Pause size={15} /> : <Play size={15} />}
            </button>
          )}
          <div className="absolute inset-x-0 bottom-2 flex items-center justify-center gap-1.5">
            {images.map((im, i) => (
              <button key={im.id} data-testid={`slide-dot-${i}`} onClick={() => go(i)} aria-label={`Go to slide ${i + 1}`}
                className={`h-2 rounded-full transition-all ${i === idx ? "w-5 bg-white" : "w-2 bg-white/60 hover:bg-white/80"}`} />
            ))}
          </div>
        </>
      )}
    </div>
  );
};
